/**
 * Faithful SVG `<filter>` to CSS `filter` translation. Conversion requires a
 * linear primitive chain, a non-clipping region, compatible colour space, and
 * safe attribute output; otherwise callers receive `null` and gate saving.
 * CSS blur uses `stdDeviation`, while drop-shadow blur uses twice that value.
 */

import type { CheerioAPI } from "cheerio";
import {
  combineColorWithOpacity,
  type DomElement,
  type DomNode,
  fmt,
  isElement,
  readAttrOrStyle,
} from "./dom";

/**
 * `<filter>` id → the CSS `filter` value that reproduces it, or `null` when
 * no CSS value does. Absent from the map means no such `<filter>` exists.
 */
export type FilterMap = ReadonlyMap<string, string | null>;

/**
 * CSS filter functions we accept when an SVG `filter` attribute is written in
 * CSS syntax directly (SVG 2 allows a `<filter-value-list>` there). Those are
 * already the target language and already sRGB, so they pass through — but
 * only these names, so the value can't smuggle a `url()` back in.
 */
const CSS_FILTER_FUNCTIONS: ReadonlySet<string> = new Set([
  "blur",
  "brightness",
  "contrast",
  "drop-shadow",
  "grayscale",
  "hue-rotate",
  "invert",
  "opacity",
  "saturate",
  "sepia",
]);

/**
 * Characters that can't appear in a value we interpolate into `style="…"`.
 * Quotes would close the attribute; `;` would start a new declaration.
 */
const CSS_VALUE_SAFE = /^[#a-zA-Z0-9 ,.%()/+-]*$/;

/** `url(#id)`, with the optional quotes CSS allows. */
const URL_REF = /\s*url\(\s*['"]?#([^)'"\s]+)['"]?\s*\)\s*/y;

/** The filter region SVG uses when the `<filter>` doesn't set one. */
const DEFAULT_REGION = { x: -0.1, y: -0.1, width: 1.2, height: 1.2 } as const;

/** Translate every `<filter>` in the document, keyed by id. */
export function collectFilters($: CheerioAPI): FilterMap {
  const filters = new Map<string, string | null>();
  $("filter").each((_i: number, el: any) => {
    const id: string | undefined = el.attribs?.id;
    // First definition wins, matching how a document resolves `url(#id)`.
    if (!id || filters.has(id)) return;
    filters.set(id, filterElementToCss(el as DomElement));
  });
  return filters;
}

/**
 * The CSS declaration for an element's own `filter`, as `"filter:…;"` or `""`.
 * Records `"filter"` in `skipped` when the effect has no faithful CSS form, so
 * the caller renders the element unfiltered *and* reports the divergence.
 */
export function filterCssFor(
  el: DomElement,
  filters: FilterMap,
  skipped: Set<string>,
): string {
  const raw = readAttrOrStyle(el, "filter")?.trim();
  if (raw === undefined || raw === "" || raw === "none") return "";
  const css = filterValueToCss(raw, filters);
  if (css === null) {
    skipped.add("filter");
    return "";
  }
  return css === "" ? "" : `filter:${css};`;
}

/**
 * Ids of every `<filter>` referenced by an element or its descendants.
 *
 * Used by the preserved-SVG fallback, which renders the source element
 * verbatim inside a fresh inline `<svg>`: the `<defs>` holding its filter was
 * dropped on the way in, leaving the attribute pointing at nothing — which
 * under SVG 1.1 means the element isn't painted at all. Re-inlining the
 * definitions removes that risk, and beats translating them: the browser runs
 * the real filter, so even chains CSS can't express render exactly.
 */
export function referencedFilterIds(root: DomElement): string[] {
  const ids: string[] = [];
  const stack: DomNode[] = [root];
  while (stack.length > 0) {
    const node = stack.pop();
    if (!node || !isElement(node)) continue;
    const raw = readAttrOrStyle(node, "filter");
    for (const id of parseUrlRefs(raw?.trim() ?? "") ?? []) {
      if (!ids.includes(id)) ids.push(id);
    }
    for (const child of node.children) stack.push(child);
  }
  return ids;
}

/** A `filter` value: either a list of `url(#id)` refs or CSS functions. */
function filterValueToCss(value: string, filters: FilterMap): string | null {
  const refs = parseUrlRefs(value);
  if (refs === null) return parseCssFilterList(value);

  const parts: string[] = [];
  for (const id of refs) {
    const css = filters.get(id);
    // A dangling reference is a spec fork: SVG 1.1 says don't render the
    // element at all, CSS Filter Effects says render it unfiltered. Lossy
    // under either reading — we can't express the first, and the second is
    // only "correct" by accident.
    if (css === undefined || css === null) return null;
    if (css !== "") parts.push(css);
  }
  return parts.join(" ");
}

function filterElementToCss(filter: DomElement): string | null {
  // `href` makes a filter inherit another's primitives. Rare, deprecated, and
  // the inherited chain would have to be spliced in to judge it — decline.
  if (filter.attribs.href !== undefined) return null;
  if (filter.attribs["xlink:href"] !== undefined) return null;
  // Primitive values in bounding-box units are fractions of a box we never
  // measure, so `stdDeviation="0.05"` can't be turned into pixels here.
  if (filter.attribs.primitiveUnits === "objectBoundingBox") return null;
  if (regionClips(filter)) return null;

  const primitives = filter.children
    .filter(isElement)
    .filter((child) => child.name.startsWith("fe"));
  // An empty filter paints transparent black — the element vanishes. As with
  // a dangling reference, that has no CSS spelling.
  if (primitives.length === 0) return null;

  const functions: string[] = [];
  let previousResult: string | undefined;
  for (const [index, primitive] of primitives.entries()) {
    if (!chainsLinearly(primitive, index, previousResult)) return null;
    const css = primitiveToCss(primitive, filter);
    if (css === null) return null;
    functions.push(css);
    previousResult = primitive.attribs.result;
  }
  return functions.join(" ");
}

/**
 * Whether the filter region is tighter than the default, i.e. the author is
 * using it to crop. Anything at or beyond the default only bounds ink CSS
 * draws in full, so it converts unchanged.
 */
function regionClips(filter: DomElement): boolean {
  // `userSpaceOnUse` puts the region in absolute user units, which can only be
  // compared to the default after measuring the element. Treat as clipping.
  const units = filter.attribs.filterUnits ?? "objectBoundingBox";
  if (units !== "objectBoundingBox") return true;

  const x = boxFraction(filter.attribs.x, DEFAULT_REGION.x);
  const y = boxFraction(filter.attribs.y, DEFAULT_REGION.y);
  const width = boxFraction(filter.attribs.width, DEFAULT_REGION.width);
  const height = boxFraction(filter.attribs.height, DEFAULT_REGION.height);
  return (
    x > DEFAULT_REGION.x ||
    y > DEFAULT_REGION.y ||
    x + width < DEFAULT_REGION.x + DEFAULT_REGION.width ||
    y + height < DEFAULT_REGION.y + DEFAULT_REGION.height
  );
}

/**
 * Whether a primitive consumes exactly the previous one's output, which is
 * what makes the chain equivalent to CSS's left-to-right function list.
 */
function chainsLinearly(
  primitive: DomElement,
  index: number,
  previousResult: string | undefined,
): boolean {
  // A second input means the graph merges two branches.
  if (primitive.attribs.in2 !== undefined) return false;
  const input = primitive.attribs.in;
  // Omitted `in` is defined as "the previous result", or the source for the
  // first primitive — the linear case, by definition.
  if (input === undefined) return true;
  if (index === 0) return input === "SourceGraphic";
  return previousResult !== undefined && input === previousResult;
}

function primitiveToCss(
  primitive: DomElement,
  filter: DomElement,
): string | null {
  switch (primitive.name) {
    case "feDropShadow":
      return dropShadowCss(primitive);
    case "feGaussianBlur":
      return inSrgb(primitive, filter) ? gaussianBlurCss(primitive) : null;
    case "feColorMatrix":
      return inSrgb(primitive, filter) ? colorMatrixCss(primitive) : null;
    case "feComponentTransfer":
      return inSrgb(primitive, filter) ? componentTransferCss(primitive) : null;
    default:
      return null;
  }
}

/**
 * `feDropShadow` → `drop-shadow()`. Note the blur conversion: CSS states this
 * function's blur radius is twice the Gaussian standard deviation.
 *
 * This is the one primitive exempt from the sRGB requirement. Its blur runs on
 * `SourceAlpha` — black at the source's alpha — and the shadow is then flooded
 * with a single flat colour. Blurring black stays black in either space and
 * alpha is never colour-converted, so the shadow itself is identical. Only the
 * final composite of shadow under *semi-transparent* source pixels differs at
 * all, which is a sub-pixel difference on antialiased edges.
 */
function dropShadowCss(primitive: DomElement): string | null {
  const deviation = stdDeviation(primitive, 2);
  if (deviation === null) return null;
  const dx = finiteNumber(primitive.attribs.dx, 2);
  const dy = finiteNumber(primitive.attribs.dy, 2);
  if (dx === null || dy === null) return null;

  const color = combineColorWithOpacity(
    readAttrOrStyle(primitive, "flood-color") ?? "black",
    readAttrOrStyle(primitive, "flood-opacity"),
  );
  if (!CSS_VALUE_SAFE.test(color)) return null;

  return `drop-shadow(${fmt(dx)}px ${fmt(dy)}px ${fmt(deviation * 2)}px ${color})`;
}

/** `feGaussianBlur` → `blur()`, whose length IS the standard deviation. */
function gaussianBlurCss(primitive: DomElement): string | null {
  const deviation = stdDeviation(primitive, 0);
  return deviation === null ? null : `blur(${fmt(deviation)}px)`;
}

/**
 * `feColorMatrix` → `saturate()` / `hue-rotate()`, which CSS defines as
 * exactly these two matrices. An explicit `matrix` could encode grayscale or
 * sepia too, but only by recognising specific coefficient sets — too easy to
 * misread a near-miss as an exact match, so those stay lossy.
 */
function colorMatrixCss(primitive: DomElement): string | null {
  const values = primitive.attribs.values?.trim();
  switch (primitive.attribs.type) {
    case "saturate": {
      const amount = values === undefined ? 1 : finiteNumber(values, 1);
      return amount === null || amount < 0 ? null : `saturate(${fmt(amount)})`;
    }
    case "hueRotate": {
      const degrees = values === undefined ? 0 : finiteNumber(values, 0);
      return degrees === null ? null : `hue-rotate(${fmt(degrees)}deg)`;
    }
    default:
      return null;
  }
}

/**
 * `feComponentTransfer` → `brightness()` / `opacity()`, the two shapes CSS
 * defines in terms of it: a linear ramp on the colour channels, or one on
 * alpha. Any other transfer function (table, gamma, discrete) is lossy.
 */
function componentTransferCss(primitive: DomElement): string | null {
  const funcs = new Map<string, DomElement>();
  for (const child of primitive.children.filter(isElement)) {
    // A repeated channel means the later one wins in ways we'd have to model.
    if (funcs.has(child.name)) return null;
    funcs.set(child.name, child);
  }

  const rgb = ["feFuncR", "feFuncG", "feFuncB"].map((name) => funcs.get(name));
  const alpha = funcs.get("feFuncA");
  if (funcs.size !== rgb.filter(Boolean).length + (alpha ? 1 : 0)) return null;

  const alphaSlope = alpha ? linearSlope(alpha) : "identity";
  const rgbSlopes = rgb.map((func) => (func ? linearSlope(func) : "identity"));
  if (alphaSlope === null || rgbSlopes.includes(null)) return null;

  // brightness(k): the same linear ramp on R, G and B, alpha untouched.
  // A ramp on only some channels has no CSS spelling.
  if (alphaSlope === "identity") {
    const [r, g, b] = rgbSlopes;
    if (r === "identity" && g === "identity" && b === "identity") {
      return "brightness(1)";
    }
    if (
      typeof r !== "number" ||
      typeof g !== "number" ||
      typeof b !== "number"
    ) {
      return null;
    }
    if (r !== g || g !== b) return null;
    return `brightness(${fmt(r)})`;
  }
  // opacity(k): a linear ramp on alpha alone.
  return rgbSlopes.every((slope) => slope === "identity")
    ? `opacity(${fmt(alphaSlope)})`
    : null;
}

/**
 * The slope of a transfer function that is a pure linear ramp through the
 * origin (what CSS's `brightness`/`opacity` expand to), `"identity"` for a
 * pass-through, or null for anything else.
 */
function linearSlope(func: DomElement): number | "identity" | null {
  const type = func.attribs.type;
  if (type === "identity") return "identity";
  if (type !== "linear") return null;
  const intercept = finiteNumber(func.attribs.intercept, 0);
  if (intercept !== 0) return null;
  const slope = finiteNumber(func.attribs.slope, 1);
  if (slope === null || slope < 0) return null;
  return slope === 1 ? "identity" : slope;
}

/**
 * Whether the primitive is computed in sRGB, which is what every CSS filter
 * function uses. SVG's own default is `linearRGB`, so silence means no.
 */
function inSrgb(primitive: DomElement, filter: DomElement): boolean {
  const property = "color-interpolation-filters";
  const own = readAttrOrStyle(primitive, property);
  return (own ?? readAttrOrStyle(filter, property)) === "sRGB";
}

/**
 * A primitive's `stdDeviation`, which may carry separate x and y deviations.
 * CSS blurs are isotropic, so an anisotropic pair is lossy; a negative one is
 * an error that disables the whole filter, which we can't express either.
 */
function stdDeviation(primitive: DomElement, fallback: number): number | null {
  const raw = primitive.attribs.stdDeviation?.trim();
  if (raw === undefined || raw === "") return fallback;
  const parts = raw.split(/[\s,]+/);
  if (parts.length > 2) return null;
  const x = finiteNumber(parts[0], Number.NaN);
  if (x === null || !Number.isFinite(x) || x < 0) return null;
  if (parts.length === 2 && finiteNumber(parts[1], Number.NaN) !== x) {
    return null;
  }
  return x;
}

/** Parse an attribute that must be a plain number, or null if it isn't. */
function finiteNumber(
  value: string | undefined,
  fallback: number,
): number | null {
  if (value === undefined || value.trim() === "") return fallback;
  const parsed = Number(value.trim());
  return Number.isFinite(parsed) ? parsed : null;
}

/** A fraction of the bounding box, written either as `0.2` or as `20%`. */
function boxFraction(value: string | undefined, fallback: number): number {
  if (value === undefined || value.trim() === "") return fallback;
  const trimmed = value.trim();
  const parsed = Number(trimmed.endsWith("%") ? trimmed.slice(0, -1) : trimmed);
  if (!Number.isFinite(parsed)) return fallback;
  return trimmed.endsWith("%") ? parsed / 100 : parsed;
}

/**
 * The ids in a value made up entirely of `url(#id)` references, or null if it
 * contains anything else (including being empty).
 */
function parseUrlRefs(value: string): string[] | null {
  const ids: string[] = [];
  let position = 0;
  while (position < value.length) {
    URL_REF.lastIndex = position;
    const match = URL_REF.exec(value);
    if (!match) return null;
    ids.push(match[1]!);
    position = URL_REF.lastIndex;
  }
  return ids.length > 0 ? ids : null;
}

/**
 * A `filter` attribute written as CSS functions rather than a reference. The
 * value is already in the target syntax, so it passes through once every
 * token is a known function and the whole string is inert.
 */
function parseCssFilterList(value: string): string | null {
  if (!CSS_VALUE_SAFE.test(value)) return null;

  const functions: string[] = [];
  let position = 0;
  while (position < value.length) {
    const head = /^\s*([a-zA-Z-]+)\(/.exec(value.slice(position));
    if (!head) {
      // Only trailing whitespace may follow the last function.
      return value.slice(position).trim() === "" && functions.length > 0
        ? functions.join(" ")
        : null;
    }
    if (!CSS_FILTER_FUNCTIONS.has(head[1]!.toLowerCase())) return null;

    let depth = 1;
    let index = position + head[0].length;
    for (; index < value.length && depth > 0; index++) {
      if (value[index] === "(") depth++;
      else if (value[index] === ")") depth--;
    }
    if (depth !== 0) return null;
    functions.push(value.slice(position, index).trim());
    position = index;
  }
  return functions.length > 0 ? functions.join(" ") : null;
}
